import { v4 } from 'uuid';
import { SourceNameWithNamespace } from 'v2/core/entities/fields';
import { UUID } from 'v2/core/entities/uuid';
import { operatorFromFilter } from 'v2/core/services/queryBuilder/operatorFromFilter';
import {
  FilterUnion,
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import checkIsExactSearchFilter from 'v2/core/utils/checkIsExactSearchFilter';
import { ROOT_UUID } from 'v2/querybuilder/constants/query';
import Conjunction from 'v2/querybuilder/types/conjunction';
import Value, {
  BooleanFilterValue,
  NumberFilterValue,
  StringFilterValue,
} from 'v2/querybuilder/types/filtervalue';
import { QBNode } from 'v2/querybuilder/types/qbNode';
import { encodeSourceToString } from './utils';

const toStringValues = (values: string[]): Value => ({
  type: 'string[]',
  values: values.map(
    (value): Required<StringFilterValue> => ({
      type: 'string',
      value,
    }),
  ),
});

const convertSourceToStringValues = (
  values: SourceNameWithNamespace[],
): Value => ({
  type: 'string[]',
  values: values.map(
    (value): Required<StringFilterValue> => ({
      type: 'string',
      value: encodeSourceToString(value),
    }),
  ),
});

const fieldValueFromFilter = (
  filter: FilterUnion,
):
  | {
      field: string;
      value: Value;
    }
  | undefined => {
  if (filter.entityFilter) {
    if (filter.entityFilter.entities?.[0]?.sentiment) {
      return {
        field: 'entitySentiment',
        value: {
          type: 'string',
          value: filter.entityFilter.entities[0].entityID,
        },
      };
    }
    return {
      field: 'entity',
      value: toStringValues(
        filter.entityFilter.entities.map(({ entityID }) => entityID),
      ),
    };
  }

  if (filter.reasonFilter) {
    return {
      field: 'reason',
      value: toStringValues(filter.reasonFilter.reasonIDs),
    };
  }

  if (filter.l1Filter) {
    return {
      field: 'l1',
      value: toStringValues(filter.l1Filter.l1IDs),
    };
  }

  if (filter.labelHierarchyFilter) {
    return {
      field: 'theme',
      value: toStringValues(filter.labelHierarchyFilter.nodeIDs),
    };
  }

  if (filter.reviewSentimentFilter) {
    return {
      field: 'reviewSentiment',
      value: toStringValues(filter.reviewSentimentFilter.sentimentIDs),
    };
  }

  if (filter.languageFilter) {
    return {
      field: 'language',
      value: toStringValues(filter.languageFilter.iso6393s),
    };
  }

  if (filter.sourceFilter) {
    return {
      field: 'source',
      value: convertSourceToStringValues(filter.sourceFilter.sources),
    };
  }

  if (filter.hybridSearchFilter) {
    if (filter.hybridSearchFilter.queries?.length) {
      const isExactSearchFilter = checkIsExactSearchFilter(
        filter.hybridSearchFilter.queries,
      );
      return {
        field: 'search',
        value: isExactSearchFilter
          ? toStringValues(
              filter.hybridSearchFilter.queries.map((q) => q.replace(/"/g, '')),
            )
          : toStringValues(filter.hybridSearchFilter.queries),
      };
    }
    return {
      field: 'search',
      value: {
        type: 'string',
        value: filter.hybridSearchFilter.query,
      },
    };
  }

  if (filter.metadataFilter) {
    switch (filter.metadataFilter.type) {
      case 'string':
        return {
          field: filter.metadataFilter.key,
          value: toStringValues(filter.metadataFilter.values),
        };

      case 'number':
        return {
          field: filter.metadataFilter.key,
          value: {
            type: 'number[]',
            values: filter.metadataFilter.values.map(
              (value): Required<NumberFilterValue> => ({
                type: 'number',
                value,
              }),
            ),
          },
        };

      case 'boolean':
        return {
          field: filter.metadataFilter.key,
          value: {
            type: 'boolean[]',
            values: filter.metadataFilter.values.map(
              (value): Required<BooleanFilterValue> => ({
                type: 'boolean',
                value,
              }),
            ),
          },
        };
    }
  }

  if (filter.hasReasonFilter) {
    return {
      field: 'reason',
      value: {
        type: 'boolean',
      },
    };
  }

  if (filter.hasAspectFilter) {
    return {
      field: 'entity',
      value: {
        type: 'boolean',
      },
    };
  }

  if (filter.hasUserContentFilter) {
    return {
      field: 'feedback',
      value: {
        type: 'boolean',
      },
    };
  }

  if (filter.categoryFilter) {
    return {
      field: 'category',
      value: toStringValues(filter.categoryFilter.categories),
    };
  }

  if (filter.hasSummaryFilter) {
    return {
      field: 'summary',
      value: {
        type: 'boolean',
      },
    };
  }

  if (filter.hasMetadataFilter) {
    return {
      field: filter.hasMetadataFilter.keys[0],
      value: {
        type: 'boolean',
      },
    };
  }

  throw Error('invalid filter');
};

const fromFilter = (
  filter: FilterUnion,
  parent: UUID,
  exclude?: boolean,
): QBNode | undefined => {
  const fieldValue = fieldValueFromFilter(filter);
  if (!fieldValue) {
    return;
  }

  const operator = operatorFromFilter(filter, exclude);

  return {
    ...fieldValue,
    id: v4(),
    type: 'filter',
    operator,
    parent,
  };
};

const toConjuction = (operatorType: OperationType): Conjunction => {
  switch (operatorType) {
    case OperationType.OR:
      return 'OR';
    case OperationType.AND:
      return 'AND';
  }
};

const toggleConjunction = (conjunction: Conjunction): Conjunction => {
  return conjunction === 'AND' ? 'OR' : 'AND';
};

const fromWhereClause = (where: WhereClause, parent?: UUID): QBNode => {
  if (where.filter) {
    if (!parent) {
      return fromWhereClause(
        {
          op: {
            type: OperationType.AND,
            params: [where],
          },
        },
        undefined,
      );
    }

    const filter = fromFilter(where.filter, parent, where.exclude);
    if (filter) {
      return filter;
    }
  }

  const conjunction = toConjuction(where.op?.type ?? OperationType.AND);

  const id = parent ? v4() : ROOT_UUID;
  return {
    id,
    parent,
    type: 'group',
    children:
      where.op?.params.map((child) =>
        fromWhereClause(
          {
            ...child,
            exclude: where.exclude ?? child.exclude,
          },
          id,
        ),
      ) ?? [],
    conjunction: where.exclude ? toggleConjunction(conjunction) : conjunction,
  };
};

export default fromWhereClause;
